import * as React from "react";
import * as crt from "./QuickJournalCrtText.css";
import { TRenderable } from "source/app/presentation/Shared/SharedTypes";
import { ShowRecentJournalEntry } from "./ShowRecentJournalEntry";
import {
    GetJournalEntryRenderKey,
    GetQuickJournalLeftPosition,
    GetRecentEntryOpacity,
} from "../QuickJournalLayout";
import { GetQuickJournalTheme, type QuickJournalTheme } from "../QuickJournalTheme";
import {
    historicalRowFadeInStaggerInMilliseconds,
    IQuickJournalEntry,
    IQuickJournalState,
    numPrevItemsToShow,
    recentEntryHeightOffset,
} from "../../Data/QuickJournalTypes";
import { IsPersistableQuickJournalEntry } from "../../Data/Entries/QuickJournalEntryNormalization";

export const ShowRecentJournalEntries = ({
    state,
    shouldFadeInHistoricalRows,
    theme,
}: {
    state: IQuickJournalState;
    shouldFadeInHistoricalRows: boolean;
    theme?: QuickJournalTheme;
}) => {
    const quickJournalTheme = GetQuickJournalTheme(theme);
    let itemsToShow: Array<IQuickJournalEntry | TRenderable | null> =
        state.recentEntries.slice(-numPrevItemsToShow);
    const totalPersistableItemsToShow = itemsToShow.filter(
        IsPersistableQuickJournalEntry
    ).length;

    if (itemsToShow.length < numPrevItemsToShow) {
        for (let index = 0; index < numPrevItemsToShow - itemsToShow.length; index++) {
            itemsToShow = [null, ...itemsToShow];
        }
    }

    return (
        <div
            className={quickJournalTheme.id === "cyberpunk" ? crt.display : undefined}
            style={{
                fontSize: "16px",
                opacity: 1,
                textAlign: "left",
                height: `${218 + recentEntryHeightOffset}px`,
                marginTop: `-${recentEntryHeightOffset}px`,
                position: "relative",
                display: "inline-block",
                marginLeft: GetQuickJournalLeftPosition(),
                transform: `translateX(${-state.horizontalOffset}px)`,
                width: "1000px",
                color: quickJournalTheme.recentEntry.color,
                textShadow: quickJournalTheme.recentEntry.textShadow,
            }}
        >
            {itemsToShow.map((entry, index) => {
                const visibleEntryIndex = itemsToShow
                    .slice(0, index + 1)
                    .filter(IsPersistableQuickJournalEntry).length - 1;
                const timeStamp =
                    entry && typeof entry === "object" && "timeStamp" in entry
                        ? entry.timeStamp
                        : "";
                const isAnimatingEntry =
                    entry &&
                    typeof entry === "object" &&
                    "createdAt" in entry &&
                    state.animatingEntryCreatedAt?.includes(
                        entry.createdAt ?? ""
                    );
                const hasVisiblePreviousMatchingTime = itemsToShow
                    .slice(0, index)
                    .some(
                        (previousEntry) =>
                            previousEntry &&
                            typeof previousEntry === "object" &&
                            "timeStamp" in previousEntry &&
                            previousEntry.timeStamp === timeStamp
                    );

                return (
                    <ShowRecentJournalEntry
                        key={GetJournalEntryRenderKey(entry, index, quickJournalTheme.id === "cyberpunk" || quickJournalTheme.id === "chromaflare")}
                        entry={entry}
                        distanceFromNewest={itemsToShow.length - 1 - index}
                        isNewSubmission={Boolean(isAnimatingEntry)}
                        opacity={
                            quickJournalTheme.id === "dune"
                                ? Math.max(
                                      GetRecentEntryOpacity(
                                          index,
                                          itemsToShow.length
                                      ),
                                      0.1
                                  )
                                : GetRecentEntryOpacity(
                                      index,
                                      itemsToShow.length
                                  )
                        }
                        hideTimeStamp={hasVisiblePreviousMatchingTime}
                        shouldFadeIn={
                            Boolean(isAnimatingEntry) ||
                            (shouldFadeInHistoricalRows &&
                                IsPersistableQuickJournalEntry(entry))
                        }
                        fadeInDelayInMilliseconds={
                            shouldFadeInHistoricalRows &&
                            IsPersistableQuickJournalEntry(entry)
                                ? Math.max(
                                      0,
                                      totalPersistableItemsToShow -
                                          1 -
                                          visibleEntryIndex
                                  ) * historicalRowFadeInStaggerInMilliseconds
                                : 0
                        }
                        theme={quickJournalTheme}
                    />
                );
            })}
        </div>
    );
};
